import React from "react";
import { Link } from "react-router-dom";
import { Nav } from "@/components/layout/Nav";
import { Footer } from "@/components/layout/Footer";
import { PageSEO } from "@/components/seo/PageSEO";
import { SITE_URL } from "@/utils/seo";
import { Clock, ArrowRight, Phone } from "lucide-react";

const SITE = SITE_URL;

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
    { "@type": "ListItem", position: 2, name: "Delivery Areas", item: `${SITE}/delivery` },
    { "@type": "ListItem", position: 3, name: "Universal Orlando", item: `${SITE}/delivery/universal-orlando` },
  ],
};

export default function UniversalDelivery() {
  return (
    <>
      <PageSEO
        title="Stroller Delivery to Universal Orlando Hotels | Enchanted Gear Rentals"
        description="Free stroller delivery to Universal Orlando resort hotels and nearby properties. Enchanted Gear Rentals is an independent family-owned service, not affiliated with Universal."
        canonical={`${SITE}/delivery/universal-orlando`}
        structuredData={breadcrumbSchema}
      />
      <Nav />
      <main className="min-h-screen" style={{ background: "#040A0F" }}>
        <section className="pt-28 pb-14 text-center" style={{ background: "linear-gradient(180deg, #060f18 0%, #040A0F 100%)" }}>
          <div className="container max-w-3xl">
            <nav aria-label="Breadcrumb" className="text-xs text-[#B9C8D5] mb-4">
              <Link to="/" className="hover:text-[#FFD166]">Home</Link>
              <span className="mx-2 opacity-40">/</span>
              <Link to="/delivery" className="hover:text-[#FFD166]">Delivery Areas</Link>
              <span className="mx-2 opacity-40">/</span>
              <span className="text-[#FFD166]">Universal Orlando</span>
            </nav>
            <h1 className="font-serif text-4xl md:text-5xl text-[#F6F1E4] mb-5">Stroller Delivery to Universal Orlando</h1>
            <p className="text-[#B9C8D5] text-lg max-w-2xl mx-auto">
              We deliver premium strollers to Universal Orlando resort hotels and nearby properties. Enchanted Gear Rentals is an independent family-owned service — not affiliated with Universal Parks &amp; Resorts.
            </p>
          </div>
        </section>

        <div className="container max-w-3xl py-12 pb-20 space-y-12">
          <section>
            <h2 className="font-serif text-2xl text-[#F6F1E4] mb-5">Universal Orlando delivery details</h2>
            <div className="space-y-4 text-[#B9C8D5] text-sm leading-relaxed">
              <p>We deliver to Universal resort hotels and on-site properties. Delivery and pickup arrangements vary by property. When you book, contact us to confirm the logistics for your specific hotel — including where to meet for handoff.</p>
              <p>Unlike Walt Disney World, Universal properties may have different policies on how third-party delivery services handle guest equipment. We will confirm the best delivery approach for your hotel before your trip.</p>
            </div>
          </section>

          <section>
            <h2 className="font-serif text-2xl text-[#F6F1E4] mb-5">Delivery windows</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { time: "7:00 am – 10:00 pm", note: "1-hour windows throughout the day" },
                { time: "Any single hour", note: "Pick the exact hour that works for you" },
                { time: "Early morning (7 am)", note: "Available at no extra charge" },
                { time: "After 10:00 pm", note: "Not available" },
              ].map(({ time, note }) => (
                <div key={time} className="flex items-center gap-3 rounded-lg bg-[#0A1825] border border-[#FFD166]/10 px-4 py-3">
                  <Clock className="w-4 h-4 text-[#FFD166] shrink-0" />
                  <div>
                    <div className="text-[#F6F1E4] font-medium text-sm">{time}</div>
                    <div className="text-xs text-[#B9C8D5]">{note}</div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="rounded-xl bg-[#0A1825] border border-[#FFD166]/15 p-6">
            <div className="flex gap-3 items-start">
              <Phone className="w-5 h-5 text-[#FFD166] shrink-0 mt-0.5" />
              <div>
                <h2 className="font-semibold text-[#F6F1E4] mb-2">Confirm your delivery logistics</h2>
                <p className="text-sm text-[#B9C8D5]">For Universal Orlando deliveries, we recommend reaching out after booking to confirm the exact meeting arrangements for your property. Call or text us at <a href="tel:+16893457368" className="text-[#FFD166]">689-345-RENT (7368)</a> or email <a href="mailto:info@enchantedgearrentals.com" className="text-[#FFD166]">info@enchantedgearrentals.com</a>.</p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="font-serif text-2xl text-[#F6F1E4] mb-4">Recommended strollers for Universal</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { slug: "zoe-v2-single", name: "Zoe V2 Single", price: 20, desc: "Lightest single — easy to fold inside indoor attractions." },
                { slug: "baby-jogger-city-mini-gt2-single", name: "Baby Jogger GT2 Single", price: 22, desc: "All-terrain, quick fold, great for long park days." },
              ].map((s) => (
                <Link key={s.slug} to={`/strollers/${s.slug}`} className="group rounded-xl bg-[#0A1825] border border-[#FFD166]/10 hover:border-[#FFD166]/40 p-4 transition-all">
                  <div className="font-serif text-[#F6F1E4] group-hover:text-[#FFD166] transition-colors">{s.name}</div>
                  <div className="text-xs text-[#B9C8D5] mt-0.5">{s.desc}</div>
                  <div className="text-[#FFD166] text-sm mt-2">From ${s.price}/day</div>
                </Link>
              ))}
            </div>
            <div className="mt-4"><Link to="/universal-orlando-stroller-rentals" className="text-sm text-[#FFD166] underline">Universal Orlando stroller guide</Link></div>
          </section>

          <div className="text-center">
            <Link to="/#trip-setup"><button className="btn-primary" data-testid="universal-delivery-cta">Reserve Your Stroller <ArrowRight className="inline w-4 h-4 ml-1" /></button></Link>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
